import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';

const LookbackTab = ({ ein, planNumber, planStatus }) => {
  const [lookbackData, setLookbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Check if plan is terminated
  const isTerminated = planStatus?.plan_status === 'TERMINATED';

  useEffect(() => {
    const fetchLookbackData = async () => {
      try {
        setLoading(true);
        const data = await api.getLookbackScenarios(ein, planNumber);
        
        setLookbackData(data);
        
        // Set the longest period as default
        if (data.lookback_scenarios && data.lookback_scenarios.length > 0) {
          const maxPeriod = Math.max(...data.lookback_scenarios.map(s => s.lookback_years));
          setSelectedPeriod(maxPeriod);
        }
      } catch (err) {
        console.error('Error fetching lookback data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (ein && planNumber) {
      fetchLookbackData();
    }
  }, [ein, planNumber]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const numValue = Number(value);
    if (isNaN(numValue)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const numValue = Number(value);
    if (isNaN(numValue)) return 'N/A';
    return `${numValue.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading historical impact data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!lookbackData || !lookbackData.lookback_scenarios || lookbackData.lookback_scenarios.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No lookback data available</div>
      </div>
    );
  }

  // Group scenarios by lookback period
  const periodGroups = {};
  lookbackData.lookback_scenarios.forEach(scenario => {
    const period = scenario.lookback_years;
    if (!periodGroups[period]) {
      periodGroups[period] = [];
    }
    periodGroups[period].push(scenario);
  });

  const periods = Object.keys(periodGroups).map(Number).sort((a, b) => b - a);
  
  // Get scenarios for selected period
  const selectedScenarios = periodGroups[selectedPeriod] || [];
  
  // Find the best scenario for the selected period (highest total economic benefit)
  const bestScenario = selectedScenarios.length > 0
    ? selectedScenarios.reduce((best, current) => {
        const bestBenefit = Number(best.total_economic_benefit) || 0;
        const currentBenefit = Number(current.total_economic_benefit) || 0;
        return currentBenefit > bestBenefit ? current : best;
      })
    : null;

  // Prepare chart data
  const chartData = selectedScenarios.map(scenario => ({
    scenario: scenario.scenario_type?.replace(/_/g, ' ') || 'Unknown',
    'Actual Balance': Number(scenario.actual_ending_balance) || 0,
    'Potential Balance': Number(scenario.scenario_ending_balance) || 0,
    'Missed Opportunity': Number(scenario.total_economic_benefit) || 0
  })).sort((a, b) => (b['Missed Opportunity'] || 0) - (a['Missed Opportunity'] || 0));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 border-2 border-orange-200 shadow-soft">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Historical Impact Analysis</h2>
        <p className="text-gray-700 leading-relaxed">
          {isTerminated 
            ? `See what participants missed out on due to high fees before the plan was terminated in ${planStatus?.termination_year}`
            : "See what your participants missed out on due to high fees in the past"
          }
        </p>
      </div>

      {/* Period Selector */}
      <div className="bg-white rounded-2xl shadow-soft p-8 border-2 border-neutral-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Lookback Period</h3>
        <div className="flex flex-wrap gap-3">
          {periods.map(period => {
            const periodData = periodGroups[period][0];
            return (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-8 py-4 rounded-xl font-medium transition-all shadow-soft ${
                  selectedPeriod === period
                    ? 'bg-teal text-white shadow-lg scale-105'
                    : 'bg-neutral-100 text-gray-700 hover:bg-neutral-200'
                }`}
              >
                <div className="text-lg">{period} Years</div>
                <div className="text-sm opacity-90">
                  {periodData.historical_start_year} - {periodData.historical_end_year}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary Cards */}
      {bestScenario && (
        <div className="space-y-6">
          {/* First Row: Total Balances */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-teal/10 to-teal/20 rounded-2xl p-6 border-2 border-teal/30 shadow-soft">
              <div className="text-sm text-teal-dark font-semibold mb-2 uppercase tracking-wide">Actual Balance</div>
              <div className="text-3xl font-bold text-teal-dark">
                {formatCurrency(bestScenario?.actual_ending_balance)}
              </div>
              <div className="text-xs text-teal mt-2">
                What you ended with
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border-2 border-green-200 shadow-soft">
              <div className="text-sm text-green-700 font-semibold mb-2 uppercase tracking-wide">Potential Balance</div>
              <div className="text-3xl font-bold text-green-900">
                {formatCurrency(bestScenario?.scenario_ending_balance)}
              </div>
              <div className="text-xs text-green-600 mt-2">
                With lower fees ({bestScenario?.scenario_type?.replace(/_/g, ' ') || 'N/A'})
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-orange-100 rounded-2xl p-6 border-2 border-coral/40 shadow-soft">
              <div className="text-sm text-red-700 font-semibold mb-2 uppercase tracking-wide">Total Missed Opportunity</div>
              <div className="text-3xl font-bold text-red-900">
                {formatCurrency(bestScenario?.total_economic_benefit)}
              </div>
              <div className="text-xs text-red-600 mt-2">
                {formatPercent(bestScenario?.benefit_as_pct_balance)} increase
              </div>
            </div>
          </div>

          {/* Second Row: Per Participant Balances */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-teal/10 to-teal/20 rounded-2xl p-6 border-2 border-teal/30 shadow-soft">
              <div className="text-sm text-teal-dark font-semibold mb-2 uppercase tracking-wide">Actual Balance Per Participant</div>
              <div className="text-3xl font-bold text-teal-dark">
                {formatCurrency(
                  lookbackData?.plan_info?.current_participants 
                    ? bestScenario?.actual_ending_balance / lookbackData.plan_info.current_participants
                    : null
                )}
              </div>
              <div className="text-xs text-teal mt-2">
                Average per participant
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border-2 border-green-200 shadow-soft">
              <div className="text-sm text-green-700 font-semibold mb-2 uppercase tracking-wide">Potential Balance Per Participant</div>
              <div className="text-3xl font-bold text-green-900">
                {formatCurrency(
                  lookbackData?.plan_info?.current_participants 
                    ? bestScenario?.scenario_ending_balance / lookbackData.plan_info.current_participants
                    : null
                )}
              </div>
              <div className="text-xs text-green-600 mt-2">
                With lower fees
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border-2 border-purple-200 shadow-soft">
              <div className="text-sm text-purple-700 font-semibold mb-2 uppercase tracking-wide">Per Participant Missed Opportunity</div>
              <div className="text-3xl font-bold text-purple-900">
                {formatCurrency(bestScenario?.benefit_per_participant)}
              </div>
              <div className="text-xs text-purple-600 mt-2">
                Average missed opportunity
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chart: Comparison Across Scenarios */}
      <div className="bg-white rounded-2xl shadow-soft p-8 border-2 border-neutral-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          Missed Opportunity by Cost Structure ({selectedPeriod} Year Period)
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
            <XAxis 
              dataKey="scenario" 
              angle={-45} 
              textAnchor="end" 
              height={100}
              tick={{ fill: '#737373' }}
            />
            <YAxis 
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              tick={{ fill: '#737373' }}
            />
            <Tooltip 
              formatter={(value) => formatCurrency(value)}
              contentStyle={{ 
                backgroundColor: 'white', 
                border: '2px solid #E5E5E5',
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend />
            <Bar dataKey="Actual Balance" fill="#12A493" radius={[8, 8, 0, 0]} />
            <Bar dataKey="Potential Balance" fill="#10b981" radius={[8, 8, 0, 0]} />
            <Bar dataKey="Missed Opportunity" fill="#FF7A5C" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-sm text-gray-600 mt-6 leading-relaxed">
          This shows how much more your plan could have grown over {selectedPeriod} years 
          with different fee structures. Coral bars show the opportunity cost of higher fees.
        </p>
      </div>

      {/* Detailed Scenario Table */}
      <div className="bg-white rounded-2xl shadow-soft p-8 border-2 border-neutral-100">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          Scenario Comparison ({selectedPeriod} Year Period)
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Period
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Alternative
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Years
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Actual Balance
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Potential Balance
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Missed Opportunity
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Per Participant
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  % Increase
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {selectedScenarios
                .sort((a, b) => (b.total_economic_benefit || 0) - (a.total_economic_benefit || 0))
                .map((scenario, idx) => (
                  <tr key={idx} className={idx === 0 ? 'bg-green-50' : 'hover:bg-neutral-50 transition-colors'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {scenario.historical_start_year}-{scenario.historical_end_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {scenario.scenario_type?.replace(/_/g, ' ').replace('PROVIDER ', '') || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {scenario.lookback_years}
                      {idx === 0 && (
                        <span className="ml-2 px-3 py-1 text-xs bg-green-600 text-white rounded-xl font-medium">
                          Best Option
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-teal-dark font-semibold">
                      {formatCurrency(scenario.actual_ending_balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-green-600">
                      {formatCurrency(scenario.scenario_ending_balance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-coral">
                      {formatCurrency(scenario.total_economic_benefit)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-purple-600 font-semibold">
                      {formatCurrency(scenario.benefit_per_participant)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-orange-600">
                      {formatPercent(scenario.benefit_as_pct_balance)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights */}
      {bestScenario && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-8 border-2 border-yellow-200 shadow-soft">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-3">💡</span>
            Key Insights
          </h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              <strong className="text-gray-900">Over the past {selectedPeriod} years</strong> ({bestScenario?.historical_start_year}-{bestScenario?.historical_end_year}), 
              participants could have had{' '}
              <strong className="text-coral text-lg">
                {formatCurrency(bestScenario?.total_economic_benefit)}
              </strong>{' '}
              more in their accounts with lower fee structures.
            </p>
            <p>
              That's an average of{' '}
              <strong className="text-purple-600 text-lg">
                {formatCurrency(bestScenario?.benefit_per_participant)}
              </strong>{' '}
              per participant that was lost to excessive fees.
            </p>
            <p>
              With better fees, your plan balance would have increased by{' '}
              <strong className="text-green-600 text-lg">
                {formatPercent(bestScenario?.benefit_as_pct_balance)}
              </strong>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookbackTab;
